import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { adminQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatDate, formatDateTime, formatMoney, LETTING_AMOUNT_LABELS } from '@blimo/shared';
import { Badge, DataTable, ErrorState, PageHeader, SearchInput } from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { PropertyLink, UnitLabel } from '../../components/data.jsx';
import { PropertyFilter } from '../finance/panels.jsx';
import { useListState } from '../../lib/list-search.js';

const OPEN_OPTIONS = [
  { value: '1', label: 'Offen' },
  { value: '0', label: 'Erledigt' },
];

const REMOVED_OPTIONS = [
  { value: '0', label: 'In Cobra geliefert' },
  { value: '1', label: 'Nicht mehr in Cobra' },
];

/** Verwaltung › Vermietung: Vermietungs-Tickets aus Cobra (read-only). Beträge und Gesamt kommen aus der API. */
export function LettingTicketsPage() {
  const api = useApi();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const open = search.open === '1' ? true : search.open === '0' ? false : undefined;
  const removed = search.removed === '1' ? true : search.removed === '0' ? false : undefined;
  const params = {
    page: list.apiParams.page,
    per_page: list.apiParams.per_page,
    filter: { property_id: search.property, open, status: search.status, removed },
  };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.lettingTickets(api, params));
  const columns = useMemo(() => lettingColumns(), []);

  return (
    <>
      <PageHeader title="Vermietung" description="Vermietungs-Tickets je Einheit (Quelle Cobra, nur lesend) mit Miete und Nebenkosten." />
      <FilterBar>
        <PropertyFilter value={search.property} onChange={(v) => list.setFilter('property', v)} />
        <FilterSelect label="Bearbeitung" value={search.open} onChange={(v) => list.setFilter('open', v)} options={OPEN_OPTIONS} />
        <FilterSelect label="Cobra" value={search.removed} onChange={(v) => list.setFilter('removed', v)} options={REMOVED_OPTIONS} />
        <SearchInput label="Status (exakt)" placeholder="Status …" value={search.status ?? ''} onValueChange={(v) => list.setFilter('status', v)} />
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Vermietungs-Tickets"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          emptyTitle="Keine Vermietungs-Tickets"
          emptyDescription="Für die gewählten Filter liegen keine Tickets vor."
        />
      )}
    </>
  );
}

const money = (key, header) => ({
  id: key,
  header,
  enableSorting: false,
  meta: { numeric: true },
  cell: ({ row }) => <span className="numeric">{formatMoney(row.original[key])}</span>,
});

function lettingColumns() {
  return [
    {
      id: 'title',
      header: 'Ticket',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span className="font-medium">{row.original.title || `Ticket #${row.original.id}`}</span>
          {row.original.cobra_id && <span className="numeric text-xs text-muted-foreground">Cobra {row.original.cobra_id}</span>}
        </span>
      ),
    },
    { id: 'property', header: 'Liegenschaft', enableSorting: false, cell: ({ row }) => <PropertyLink property={row.original.property} /> },
    { id: 'unit', header: 'Einheit', enableSorting: false, cell: ({ row }) => <UnitLabel unit={row.original.unit} /> },
    {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex flex-col gap-0.5">
          <Badge variant={row.original.is_open ? 'warning' : 'success'}>{row.original.is_open ? 'Offen' : 'Erledigt'}</Badge>
          {row.original.source_removed_at && (
            <Badge variant="outline" title={`Seit ${formatDateTime(row.original.source_removed_at)} nicht mehr von Cobra geliefert`}>
              Nicht mehr in Cobra
            </Badge>
          )}
          {(row.original.status || row.original.status_complete) && (
            <span className="text-xs text-muted-foreground">{[row.original.status, row.original.status_complete].filter(Boolean).join(' · ')}</span>
          )}
        </span>
      ),
    },
    money('rent', LETTING_AMOUNT_LABELS.rent),
    money('garage_rent', LETTING_AMOUNT_LABELS.garage_rent),
    money('operating_costs', LETTING_AMOUNT_LABELS.operating_costs),
    money('heating_costs', LETTING_AMOUNT_LABELS.heating_costs),
    {
      id: 'total',
      header: 'Gesamt',
      enableSorting: false,
      meta: { numeric: true },
      cell: ({ row }) => <span className="numeric font-medium">{formatMoney(row.original.total)}</span>,
    },
    {
      id: 'termination',
      header: 'Kündigung bestätigt',
      enableSorting: false,
      cell: ({ row }) => <span className="numeric">{formatDate(row.original.termination_confirmed_on)}</span>,
    },
    {
      id: 'updated_at',
      header: 'Stand',
      enableSorting: false,
      cell: ({ row }) => <span className="numeric text-muted-foreground">{formatDateTime(row.original.updated_at)}</span>,
    },
  ];
}
